import i18next, { type TOptions } from 'i18next';
import BACKEND from 'i18next-http-backend';
import ICU from 'i18next-icu';

import { useGlobalStore } from '@/stores/global';
import { useLocaleStore } from '@/stores/locales';
import type { InContextData } from '@/types/i18n/i18n.types';

const icuGender = (shortGender: string): string => {
  let gender = 'female';
  if (shortGender === 'm') {
    gender = 'male';
  } else if (shortGender === 'n') {
    gender = 'other';
  }
  return gender;
};

// Setup translation module
const i18nextLib = i18next.use(ICU).use(BACKEND);

// Load default lang
function initI18N(localeId = 1, InContextData: InContextData | false = false) {
  const globalStore = useGlobalStore();
  const localeStore = useLocaleStore();

  return new Promise<void>(resolve => {
    // Fetch locales list
    localeStore.setLocale(localeId);
    localeStore.loadLocales().then(() => {
      // Get ISO list from store
      const supportedLngs = localeStore.getISOLocales;
      let lang = localeStore.getCurrentISO;

      if (typeof InContextData === 'object' && InContextData.pseudoLang) {
        supportedLngs.push(InContextData.pseudoLang);
        lang = InContextData.pseudoLang;
      }

      // Get path to load resources
      const loadPath = `${globalStore.translation_url}/{{lng}}/{{ns}}.json`;

      // Init 18next
      i18nextLib
        .init({
          ns: [
            // Add every traduction files here
            'accessibility',
            'admin',
            'email',
            'affinity',
            'ambassador',
            'brain_report',
            'brain_summary',
            'campaigns',
            'candidate',
            'candidate_profile',
            'compatibility_report',
            'competency_framework',
            'competency_framework_group',
            'competency_framework_individual',
            'data_demographics',
            'data_jobs',
            'drive_definitions',
            'drive_dimensions',
            'drive_report',
            'drive_summary',
            'email',
            'error',
            'general',
            'jobs',
            'language',
            'leadership',
            'manager_account',
            'navigation',
            'notifications',
            'personality',
            'pole_emploi',
            'plans',
            'predictive_models',
            'predict_success',
            'profile_other',
            'report',
            'recruiter_network',
            'recruiter_profile',
            'shape_definitions',
            'shape_dimensions',
            'shape_summary',
            'skills',
            'tools',
            'sourcing',
            'talent_cloud',
            'talent_tags',
            'talentmap',
            'talentmap_report',
            'team_analysis',
            'team_analysis_report',
            'territories',
            'values',
            'voice',
            'voice_summary',
            'pole_emploi',
            'career_paths',
            'values',
            'disc'
          ],
          lng: lang,
          supportedLngs,
          fallbackLng: {
            default: ['en-US']
          },
          keySeparator: '.',
          debug: false,
          cache: {
            enabled: true,
            prefix: 'i18next_res_',
            expirationTime: 7 * 24 * 60 * 60 * 1000
          },
          backend: {
            loadPath,
            parse(data) {
              return JSON.parse(data);
            }
          },
          i18nFormat: {
            parseLngForICU: lng => {
              return `${lng}-u-ca-gregory-nu-latn`;
            }
          }
        })
        .then(() => {
          resolve();
        });
    });
  });
}

// function to format our translation keys into i18n format
const t = (
  key: string,
  options: undefined | Omit<TOptions, 'context'> = undefined,
  checkShouldExist = false
): string => {
  const newKey = key.replace('.', ':');
  // If needed, check if the key exists in the language files, if not return raw text
  if (checkShouldExist && !i18next.exists(newKey)) {
    return key;
  }

  // If gender passed, format in icu format
  if (options && options.gender) {
    options.gender = icuGender(options.gender as string);
  }
  if (!options) {
    return i18next.t(newKey);
  }

  return i18next.t(newKey, options);
};

// Add tg function to use gender key
// genderized keys work the same way as anon keys, so anon can be passsed as a value for the gender
const tg = (key: string, gender: 'm' | 'f' | 'n' | 'anon' | undefined, options: undefined | TOptions = undefined) => {
  if (gender) {
    key = `${key}_${gender}`;
  } else {
    key = `${key}_n`;
  }
  return t(key, options);
};

export { i18next, initI18N, t, tg };
